/* The Additional Sheets card pages, drawn at their measured positions: ancestry,
   community and subclass boxes beside domain card boxes, then pages of ten domain card
   boxes for the cards gained by levelling up. Filled boxes show the card text; empty ones
   keep their write-in lines. */

import { Fragment, ReactNode } from 'react';
import type { SheetSection } from './content/sheetText';
import type { DomainCard } from './content/domains/cards';
import { ancestryFeatures, Character } from './model';
import { multiclassCard, ownedCards, SubclassCard, subclassCards } from './leveling';
import { emphasize, PageProps } from './SheetParts';
import { Banner, Box, cutBox, EditGears, focusBox, GearMark, Highlights, Html, PageFooter, SheetSvg, Txt, Value, WriteLine } from './svg';

const ROW = 152.3;
const COL = 297.5;
const FIRST_PAGE_CARDS = 5;
const PAGE_CARDS = 10;
const TIERS: SubclassCard['tier'][] = ['Foundation', 'Specialization', 'Mastery'];

const AREAS: Partial<Record<SheetSection, Box>> = {
  heritage: [10, 10, 298, 452],
  domainCards: [308, 10, 298, 300],
};

const GEARS: Partial<Record<SheetSection, [number, number]>> = {
  heritage: [294, 25.7],
  domainCards: [591.5, 25.7],
};

/** The box frame and its banner; `col` 0 is left, 1 is right. */
function CardFrame({ col, row, title, name }: { col: number; row: number; title: string; name?: string }) {
  const dx = col * COL;
  const dy = row * ROW;
  return (
    <g>
      <path className="o-frame" d={cutBox(16.5 + dx, 25.7 + dy, 301.7 + dx, 154.4 + dy, 3.6)} />
      <Banner
        x0={35.7 + dx}
        x1={282.5 + dx}
        y={16.2 + dy}
        h={20}
        tab={null}
        wings={[31.3 + dx, 286.9 + dx]}
        align="left"
        title={<><tspan>{title}: </tspan><tspan fontWeight={400} fontSize={9}>{name}</tspan></>}
      />
    </g>
  );
}

function Label({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return <Txt x={x} y={y} size={6.4} weight={800}>{children}</Txt>;
}

/** Feature text when filled; otherwise the box's feature lines and labels. */
function FeatureArea({ dy, top, bottom, lines, labels, children }: { dy: number; top: number; bottom: number; lines: number[]; labels: number[]; children?: ReactNode }) {
  if (children) {
    return (
      <g>
        <Label x={33.3} y={top + 11 + dy}>FEATURE</Label>
        <Html x={33} y={top + 13 + dy} w={258} h={bottom - top - 15} className="cs-card-text" fit>{children}</Html>
      </g>
    );
  }
  return (
    <g>
      {lines.map((y) => <WriteLine key={y} x0={25.4} x1={292.9} y={y + dy} />)}
      {labels.map((y) => <Label key={y} x={33.3} y={y + dy}>FEATURE</Label>)}
    </g>
  );
}

/** Elementalist's element, written after the feature; on screen it opens the element dialog, with the sheet's gear. */
function ElementLine({ c, onChoose }: Pick<PageProps, 'c' | 'onChoose'>) {
  const text = <><strong>Element: </strong><span className="cs-element__line">{c.element}</span></>;
  if (!onChoose) return <p className="cs-element">{text}</p>;
  return (
    <button type="button" className="cs-element cs-edit" aria-label="Choose your element" title="Choose your element" onClick={() => onChoose('element')}>
      {text}<GearMark label="Choose your element" />
    </button>
  );
}

function SubclassBox({ row, card, c, onChoose }: { row: number; card?: SubclassCard; c: Character; onChoose?: PageProps['onChoose'] }) {
  const dy = (row - 2) * ROW;
  return (
    <g>
      <CardFrame col={0} row={row} title="SUBCLASS" name={card && `${card.subclass}${row > 2 && card.tier === 'Foundation' ? ` (${card.className})` : ''}`} />
      <path className="s-frame" d={`M46.5 ${348.2 + dy}H271.5L278 ${354.8 + dy}V${356.7 + dy}L271.5 ${363.2 + dy}H46.5L39.9 ${356.7 + dy}V${354.8 + dy}Z`} />
      {[44.5, 122.4, 216.1].map((x, i) => (
        <rect key={x} className={card?.tier === TIERS[i] ? 's-band' : 's-paper'} x={x + 1.6} y={351.9 + dy} width={7.4} height={7.4} rx={0.8} transform={`rotate(45 ${x + 5.3} ${355.6 + dy})`} />
      ))}
      <Txt x={57.3} y={359 + dy} size={9} weight={700} tone="frame">FOUNDATION</Txt>
      <Txt x={135.2} y={359 + dy} size={9} weight={700} tone="frame">SPECIALIZATION</Txt>
      <Txt x={228.9} y={359 + dy} size={9} weight={700} tone="frame">MASTERY</Txt>
      <WriteLine x0={25.1} x1={292.6} y={383.9 + dy} />
      <Value x={33} base={385.3 + dy} w={120}>{card?.className}</Value>
      <Value x={164} base={385.3 + dy} w={120}>{card?.spellcastTrait}</Value>
      <Label x={33.1} y={394.8 + dy}>CLASS</Label>
      <Label x={164} y={394.8 + dy}>SPELLCAST TRAIT</Label>
      <FeatureArea dy={dy} top={407.1} bottom={459} lines={[407.7, 433.6]} labels={[418.1]}>
        {card ? card.features.map((f, i) => (
          <Fragment key={i}>
            <p>{f.name && <strong>{f.name}: </strong>}{emphasize(f.description ?? '')}</p>
            {f.name === 'Elementalist' && <ElementLine c={c} onChoose={onChoose} />}
          </Fragment>
        )) : undefined}
      </FeatureArea>
    </g>
  );
}

function DomainBox({ col, row, card }: { col: number; row: number; card?: DomainCard }) {
  const dx = (col - 1) * COL;
  const dy = row * ROW;
  return (
    <g>
      <CardFrame col={col} row={row} title="DOMAIN CARD" name={card?.name} />
      <WriteLine x0={322.4 + dx} x1={589.9 + dx} y={55.4 + dy} />
      <Value x={330 + dx} base={56.8 + dy} w={110}>{card?.domain}</Value>
      <Value x={445 + dx} base={56.8 + dy} w={110}>{card?.type}</Value>
      <Value x={558 + dx} base={56.8 + dy} w={30}>{card?.recallCost}</Value>
      <Label x={330.3 + dx} y={66.3 + dy}>DOMAIN</Label>
      <Label x={445.3 + dx} y={66.3 + dy}>TYPE</Label>
      <Label x={558.1 + dx} y={66.3 + dy}>RECALL</Label>
      {card ? (
        <>
          <Label x={330.3 + dx} y={78 + dy}>FEATURE</Label>
          <Html x={330 + dx} y={80 + dy} w={259} h={72} className="cs-card-text" fit>{card.rendered}</Html>
        </>
      ) : (
        <>
          {[79.3, 103.1, 129].map((y) => <WriteLine key={y} x0={322.4 + dx} x1={589.9 + dx} y={y + dy} />)}
          <Label x={330.3 + dx} y={90.2 + dy}>FEATURE</Label>
        </>
      )}
    </g>
  );
}

/** The transformation, boxed like the ancestry card (it is taken "as if it were an aspect of your heritage") on its own last page. */
export function TransformationPage({ c, catalog }: PageProps) {
  const t = catalog.transformation(c.transformation);
  if (!t) return null;
  return (
    <SheetSvg label="Transformation" className="cs-cards">
      <CardFrame col={0} row={0} title="TRANSFORMATION" name={t.name} />
      <FeatureArea dy={0} top={44} bottom={154.4} lines={[]} labels={[]}>
        {[t.benefit, t.drawback].map((f) => <p key={f.name}><strong>{f.name}: </strong>{emphasize(f.text)}</p>)}
      </FeatureArea>
      <PageFooter hf={catalog.cls(c.className)?.source === 'HF'} x={15.1} />
    </SheetSvg>
  );
}

export default function CardsPage({ c, catalog, highlight, closeUp, onEdit, onChoose }: PageProps) {
  const cls = catalog.cls(c.className);
  const community = catalog.community(c.community);
  const multi = multiclassCard(c, catalog);
  const subclasses = [...subclassCards(c, catalog), ...(multi ? [multi] : [])];
  const cards = ownedCards(c).map((name) => catalog.card(name)).filter((k): k is DomainCard => !!k);
  const later = cards.slice(FIRST_PAGE_CARDS);
  const features = (list: Array<{ name?: string; text: ReactNode }>) =>
    list.length ? list.map((f, i) => <p key={i}>{f.name && <strong>{f.name}: </strong>}{f.text}</p>) : undefined;

  return (
    <>
      <SheetSvg label="Heritage and domain cards" className="cs-cards" view={closeUp ? focusBox(AREAS, highlight) : undefined}>
        <CardFrame col={0} row={0} title="ANCESTRY" name={[c.ancestry, c.mixedAncestry].filter(Boolean).join(' / ')} />
        <FeatureArea dy={0} top={44} bottom={154.4} lines={[56.1, 80, 103.9, 130.2]} labels={[67, 115.3]}>
          {features(ancestryFeatures(c, catalog))}
        </FeatureArea>
        <CardFrame col={0} row={1} title="COMMUNITY" name={c.community} />
        <FeatureArea dy={0} top={196.4} bottom={306.7} lines={[208.4, 232.2, 256.1, 282.4]} labels={[219.3]}>
          {features(community?.features ?? [])}
        </FeatureArea>
        {[2, 3, 4].map((row, k) => <SubclassBox key={row} row={row} card={subclasses[k]} c={c} onChoose={onChoose} />)}
        {[0, 1, 2, 3, 4].map((row) => <DomainBox key={row} col={1} row={row} card={cards[row]} />)}
        <PageFooter hf={cls?.source === 'HF'} x={15.1} />
        <Highlights boxes={AREAS} on={highlight} />
        <EditGears at={GEARS} onEdit={onEdit} />
      </SheetSvg>
      {Array.from({ length: Math.ceil(later.length / PAGE_CARDS) }, (_, page) => (
        <SheetSvg key={page} label="Domain cards, continued" className="cs-cards">
          {Array.from({ length: PAGE_CARDS }, (_, i) => (
            <DomainBox key={i} col={i % 2} row={Math.floor(i / 2)} card={later[page * PAGE_CARDS + i]} />
          ))}
          <PageFooter hf={cls?.source === 'HF'} x={15.1} />
        </SheetSvg>
      ))}
    </>
  );
}
